import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import {
  BsDiscord,
  BsEnvelope,
  BsEnvelopePlus,
  BsFacebook,
  BsFileEarmarkBreak,
  BsGithub,
  BsLinkedin,
  BsNewspaper,
  BsSkype,
  BsSlack,
  BsSpotify,
  BsTelephone,
  BsTelephonePlus,
  BsTiktok,
  BsYoutube,
} from 'react-icons/bs'
import { RiTwitterXFill } from 'react-icons/ri'
import { useSelector } from 'react-redux'
import {
  ContactAddressType,
  ExternalNumber,
} from '../../../../../common/ContactAddress.type'
import userRole from '../../../../../config/userRole'
import { useProcessContactAddress } from '../../../../../hooks/fetch/contactAddress/useProcessContactAddress'
import { selectRole } from '../../../../../slices/authSlice'
import NowLoading from '../../../../UtilComponent/level_1/NowLoading'
import { useQueryContactAddress } from '../../../../../hooks/fetch/contactAddress/useQueryContactAddress'
import { getLabelText, getUiLabelItems } from '../../../../../common/PhoneLabelItems'


const buildDisplayNumbers = (c?: ContactAddressType): ExternalNumber[] => {
  const base = c?.externalNumbers ?? []
  const exists = new Set(
    base.map((x) => (x.externalPhoneNumber ?? '').trim()).filter((x) => x !== ''),
  )
  const merged: ExternalNumber[] = [...base]

  const publicNum = (c?.phone ?? '').trim()
  if (publicNum !== '' && !exists.has(publicNum)) {
    merged.unshift({
      externalNumberId: undefined,
      externalPhoneNumber: publicNum,
      labelCode: 1,
      customLabel: '',
      displayOrder: 1,
      updCount: undefined,
    })
    exists.add(publicNum)
  }

  const mobileNum = (c?.mobilePhone ?? '').trim()
  if (mobileNum !== '' && !exists.has(mobileNum)) {
    const insertIndex = publicNum !== '' ? 1 : 0
    merged.splice(insertIndex, 0, {
      externalNumberId: undefined,
      externalPhoneNumber: mobileNum,
      labelCode: 4,
      customLabel: '',
      displayOrder: insertIndex + 1,
      updCount: undefined,
    })
    exists.add(mobileNum)
  }

  return merged.map((row, i) => ({ ...row, displayOrder: i + 1 }))
}

const ContactAddressDetail = () => {
  const { id } = useParams()
  const [ContactAddress, setContactAddress] = useState<ContactAddressType>()
  const { data: contactAddresses } = useQueryContactAddress()

  useEffect(() => {
    if (contactAddresses !== undefined) {
      setContactAddress(
        contactAddresses.filter(
          (contactAddress) => contactAddress.id.toString() === (id as string),
        )[0],
      )
    } else {
      setContactAddress({} as ContactAddressType)
    }
  }, [contactAddresses, id])

  const { deleContactAddress } = useProcessContactAddress()
  const role = useSelector(selectRole)

  const displayNumbers = buildDisplayNumbers(ContactAddress)

  const allowWireless = getUiLabelItems().some((x) => x.code === 8)

  return ContactAddress ? (
    <div className="box w-contacts-box">
      <div className="switcher">
        <div>
          {(ContactAddress.lastNameKana || ContactAddress.firstNameKana) && (
            <div id="fullNameKana" className="switcher text-mini">
              {ContactAddress.lastNameKana} {ContactAddress.firstNameKana}
            </div>
          )}

          <div id="fullName" className="switcher">
            <span className="fw-bold">
              {ContactAddress.lastName} {ContactAddress.firstName}
            </span>
          </div>

          {ContactAddress.organization && (
            <div id="organization" className="switcher">
              <span>
                <span className="me-1">組織:</span>
                <span className="fw-bold">{ContactAddress.organization}</span>
              </span>
            </div>
          )}

          {ContactAddress.department && (
            <div id="department" className="switcher">
              <span>
                <span className="me-1">部署:</span>
                <span className="fw-bold">{ContactAddress.department}</span>
              </span>
            </div>
          )}

          {ContactAddress.position && (
            <div id="position" className="switcher">
              <span>
                <span className="me-1">役職:</span>
                <span className="fw-bold">{ContactAddress.position}</span>
              </span>
            </div>
          )}

          {displayNumbers
            .slice()
            .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
            .filter((row) => {
              if (!allowWireless && row.labelCode === 8) return false
              return true
            })
            .map((row, idx) => {
              const labelText = getLabelText(row.labelCode, row.customLabel)

              const phonePrefix =
                (labelText ?? '').trim() === '' ? '電話:' : `電話(${labelText}):`

              return (
                <div key={row.externalNumberId ?? idx} className="switcher">
                  <span>
                    <BsTelephone size={'1rem'} />
                    <span className="me-1">{phonePrefix}</span>
                    <span className="fw-bold">{row.externalPhoneNumber}</span>
                  </span>
                </div>
              )
            })}

          {ContactAddress.telExtension && (
            <div id="telExtension" className="switcher">
              <span>
                <BsTelephonePlus size={'1rem'} />
                <span className="me-1">内線番号:</span>
                <span className="fw-bold">{ContactAddress.telExtension}</span>
              </span>
            </div>
          )}

          {ContactAddress.email && (
            <div id="email" className="switcher">
              <span>
                <BsEnvelope size={'1rem'} />
                <span className="me-1">Eメール:</span>
                <span className="fw-bold">{ContactAddress.email}</span>
              </span>
            </div>
          )}

          {ContactAddress.altEmail && (
            <div id="altEmail" className="switcher">
              <span>
                <BsEnvelopePlus size={'1rem'} />
                <span className="me-1">その他Eメール:</span>
                <span className="fw-bold">{ContactAddress.altEmail}</span>
              </span>
            </div>
          )}

          {ContactAddress.fax && (
            <div id="fax" className="switcher">
              <span>
                <BsNewspaper size={'1rem'} />
                <span className="me-1">FAX:</span>
                <span className="fw-bold">{ContactAddress.fax}</span>
              </span>
            </div>
          )}

          {ContactAddress.country && (
            <div id="country" className="switcher">
              <span>
                <span className="me-1">国:</span>
                <span className="fw-bold">{ContactAddress.country}</span>
              </span>
            </div>
          )}

          {ContactAddress.prefecture && (
            <div id="prefecture" className="switcher">
              <span>
                <span className="me-1">都道府県:</span>
                <span className="fw-bold">{ContactAddress.prefecture}</span>
              </span>
            </div>
          )}

          {ContactAddress.city &&
            ContactAddress.address1 &&
            ContactAddress.address2 && (
              <div id="address" className="switcher">
                <span>
                  <span className="me-1">住所:</span>
                  <span className="fw-bold">
                    {ContactAddress.city}
                    {ContactAddress.address1}
                    {ContactAddress.address2}
                  </span>
                </span>
              </div>
            )}
        </div>

        <div>
          {ContactAddress.site && (
            <div id="site" className="switcher">
              <span>
                <BsFileEarmarkBreak size={'1rem'} />
                <span className="me-1">サイト:</span>
                <span className="fw-bold">{ContactAddress.site}</span>
              </span>
            </div>
          )}

          {ContactAddress.linkedIn && (
            <div id="linkedIn" className="switcher">
              <span>
                <BsLinkedin size={'1rem'} />
                <span className="me-1">LinkedIn:</span>
                <span className="fw-bold">{ContactAddress.linkedIn}</span>
              </span>
            </div>
          )}

          {ContactAddress.facebook && (
            <div id="facebook" className="switcher">
              <span>
                <BsFacebook size={'1rem'} />
                <span className="me-1">Facebook:</span>
                <span className="fw-bold">{ContactAddress.facebook}</span>
              </span>
            </div>
          )}

          {ContactAddress.twitter && (
            <div id="twitter" className="switcher">
              <span>
                <RiTwitterXFill size={'1rem'} />
                <span className="me-1">X(Twitter):</span>
                <span className="fw-bold">{ContactAddress.twitter}</span>
              </span>
            </div>
          )}

          {ContactAddress.skype && (
            <div id="skype" className="switcher">
              <span>
                <BsSkype size={'1rem'} />
                <span className="me-1">Skype:</span>
                <span className="fw-bold">{ContactAddress.skype}</span>
              </span>
            </div>
          )}

          {ContactAddress.slack && (
            <div id="slack" className="switcher">
              <span>
                <BsSlack size={'1rem'} />
                <span className="me-1">Slack:</span>
                <span className="fw-bold">{ContactAddress.slack}</span>
              </span>
            </div>
          )}

          {ContactAddress.github && (
            <div id="github" className="switcher">
              <span>
                <BsGithub size={'1rem'} />
                <span className="me-1">Github:</span>
                <span className="fw-bold">{ContactAddress.github}</span>
              </span>
            </div>
          )}

          {ContactAddress.youtube && (
            <div id="youtube" className="switcher">
              <span>
                <BsYoutube size={'1rem'} />
                <span className="me-1">YouTube:</span>
                <span className="fw-bold">{ContactAddress.youtube}</span>
              </span>
            </div>
          )}

          {ContactAddress.tiktok && (
            <div id="tiktok" className="switcher">
              <span>
                <BsTiktok size={'1rem'} />
                <span className="me-1">TikTok:</span>
                <span className="fw-bold">{ContactAddress.tiktok}</span>
              </span>
            </div>
          )}

          {ContactAddress.spotify && (
            <div id="spotify" className="switcher">
              <span>
                <BsSpotify size={'1rem'} />
                <span className="me-1">Spotify:</span>
                <span className="fw-bold">{ContactAddress.spotify}</span>
              </span>
            </div>
          )}

          {ContactAddress.discord && (
            <div id="discord" className="switcher">
              <span>
                <BsDiscord size={'1rem'} />
                <span className="me-1">Discord:</span>
                <span className="fw-bold">{ContactAddress.discord}</span>
              </span>
              <div className="col-sm"></div>
            </div>
          )}
        </div>
      </div>

      {(role === userRole['カンパニー管理者'] ||
        ContactAddress.category === 'personal') && (
        <div className="switcher mt-3">
          <button type="button" className="submitButton ms-3">
            <Link to={`/contactAddress/${ContactAddress.id}/edit`}>編集する</Link>
          </button>

          <button
            type="button"
            className="submitButton btn-danger ms-3"
            onClick={(e) => {
              let wkName: string | undefined =
                ContactAddress.lastName + ContactAddress.firstName
              if (wkName === undefined || wkName === '') {
                wkName = ContactAddress.contactName
              }
              const isOK = window.confirm(`${wkName}さんを削除してもよろしいでしょうか`)
              if (isOK) {
                deleContactAddress(e, ContactAddress.id)
              }
            }}
          >
            削除する
          </button>
        </div>
      )}
    </div>
  ) : (
    <NowLoading />
  )
}

export default ContactAddressDetail
